/**
 * Login modal for the public landing page.
 *
 * This is a UI relocation only — it calls the exact same `useAuth().login()`
 * function `LoginPage.tsx` uses (same request, same token storage, same
 * error handling via `extractErrorMessage`). No authentication logic lives
 * here. `/login` still exists as its own standalone route (used by
 * `ProtectedRoute` and the axios interceptor for redirect-back flows); this
 * modal is just a second, marketing-page-friendly entry point into the same
 * flow.
 */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import useAuth from "@/hooks/useAuth";
import { extractErrorMessage, getErrorCode } from "@/utils/errors";
interface LoginFormValues {
  email: string;
  password: string;
}
interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}
export function LoginModal({ open, onClose }: LoginModalProps) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LoginFormValues>();
  const handleClose = () => {
    setServerError(null);
    setUnverifiedEmail(null);
    reset();
    onClose();
  };
  const onSubmit = async (values: LoginFormValues) => {
    setServerError(null);
    setUnverifiedEmail(null);
    setIsSubmitting(true);
    try {
      await login(values.email, values.password);
      reset();
      onClose();
      navigate("/dashboard", { replace: true });
    } catch (error) {
      if (getErrorCode(error) === "UNVERIFIED_ACCOUNT") {
        setUnverifiedEmail(values.email);
      }
      setServerError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <IconButton
        aria-label="Close"
        onClick={handleClose}
        sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent sx={{ px: 4, pt: 5, pb: 4 }}>
        <Stack alignItems="center" spacing={0.5} mb={3}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShowChartIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              TekInvest
            </Typography>
          </Stack>
          <Typography variant="h5" fontWeight={700}>
            Welcome Back
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to access your dashboard
          </Typography>
        </Stack>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            {unverifiedEmail && (
              <Link
                component={RouterLink}
                to={`/resend-verification?email=${encodeURIComponent(unverifiedEmail)}`}
                variant="body2"
                onClick={handleClose}
              >
                Resend verification email
              </Link>
            )}
            <TextField
              label="Email"
              type="email"
              fullWidth
              autoComplete="email"
              autoFocus
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...register("email", { required: "Email is required" })}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              autoComplete="current-password"
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              {...register("password", { required: "Password is required" })}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <FormControlLabel control={<Checkbox size="small" />} label="Remember me" />
              <Link component={RouterLink} to="/forgot-password" variant="body2" onClick={handleClose}>
                Forgot password?
              </Link>
            </Stack>
            <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
              {isSubmitting ? "Signing in..." : "Sign In"}
            </Button>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              New to TekStar Invest?{" "}
              <Link component={RouterLink} to="/register" onClick={handleClose}>
                Create free account
              </Link>
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
export default LoginModal;
